import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowRightLeft, ClipboardPaste, Minus, Plus } from 'lucide-react'
import { restoreItem } from '../lib/db'
import { Banner, Button, Checkbox, Chip, Field, IconButton, Row, Sheet, useToast } from '../ui'
import { useAddItem, useBumpUsual, useRemoveItem, useSetItemDone, useUpdateItem, useUpdateListShop } from '../lib/mutations'
import { usualsToOffer } from '../lib/usuals'
import { useListUsuals } from '../lib/queries'
import { useWriteError } from '../lib/useAction'
import { aisleOf, AISLES } from '../lib/aisles'
import { useAisleDict, useGroceryAisles, useSetAisleOverride } from '../lib/groceryAisles'
import { formatLine, parseQty } from '../lib/listParse'
import { addGrocery } from '../lib/listAdd'
import { topPosition } from '../lib/newPosition'
import { useBlurField } from '../lib/useBlurField'
import { useLinger } from '../lib/useLinger'
import { foldFinished } from '../lib/finished'
import type { List, ListItem } from '../lib/types'
import { RowMore } from './RowMore'
import { RecipeImportSheet } from './RecipeImportSheet'

/** L3's editable aisle order, per list. `lists.shop` is display only (the
 *  migration's own comment) and there is no `list_settings` column in the
 *  4.1 schema for a second per-list preference, so this one lives beside it
 *  in `localStorage` — a per-device choice, which is an acceptable trade for
 *  a shop-walk order Doug sets once and rarely touches again. */
const orderKey = (listId: string) => `sl.aisleOrder.${listId}`

function loadOrder(listId: string): string[] {
  try {
    const raw = localStorage.getItem(orderKey(listId))
    if (!raw) return AISLES
    const saved = (JSON.parse(raw) as string[]).filter(a => AISLES.includes(a))
    // An aisle missing from a saved order — 'Other', or one added since it
    // was written — rides at the end in the default order rather than
    // vanishing from the screen.
    return [...saved, ...AISLES.filter(a => !saved.includes(a))]
  } catch { return AISLES }
}

function saveOrder(listId: string, order: string[]) {
  try { localStorage.setItem(orderKey(listId), JSON.stringify(order)) } catch { /* private mode, full quota — the default order still works */ }
}

const MAX_USUALS = 12

export function GroceryList({ list, items, recipe = null, onRecipeDone }: {
  list: List; items: ListItem[]
  /** B2's Shortcut route: text the hash brought, or `'clipboard'` — opens
   *  the recipe sheet on mount with it. */
  recipe?: string | null
  onRecipeDone?: () => void
}) {
  const toast = useToast()
  const qc = useQueryClient()
  const [draft, setDraft] = useState('')
  const [edit, setEdit] = useState<string | null>(null)
  const [shopEdit, setShopEdit] = useState(false)
  const [shop, setShop] = useState(list.shop ?? '')
  const [order, setOrder] = useState(() => loadOrder(list.id))
  const [drag, setDrag] = useState<{ aisle: string; over: string | null } | null>(null)
  const [moveItem, setMoveItem] = useState<ListItem | null>(null)
  const [recipeOpen, setRecipeOpen] = useState(recipe != null)
  useEffect(() => { if (recipe != null) setRecipeOpen(true) }, [recipe])

  const addM = useAddItem()
  const bumpM = useBumpUsual()
  const toggleM = useSetItemDone()
  const removeM = useRemoveItem()
  const updateM = useUpdateItem()
  const shopM = useUpdateListShop()
  const setAisleM = useSetAisleOverride()
  const usualsQ = useListUsuals(list.id)
  const aislesQ = useGroceryAisles()
  const known = aislesQ.data ?? []
  // Package A: the 22k-term dictionary, its own chunk; `null` for the frame
  // or two before it arrives, during which every row sits in Other.
  const dict = useAisleDict()
  const error = useWriteError(addM, toggleM, removeM, updateM, shopM, setAisleM)
  const busy = addM.isPending

  // The after-checked state, one rule for every kind (`lib/finished.ts`): a
  // ticked row lingers in its aisle, struck through, then leaves for the
  // basket, which is drawn newest first.
  const linger = useLinger()
  const { open, finished: basket } = foldFinished('grocery', items, linger.lingering)
  // A tick the server refused: the row is back open (the banner says why),
  // so no linger may go on to collapse it.
  useEffect(() => { if (toggleM.isError) linger.cancelAll() }, [toggleM.isError, linger])
  // Not what is already open on the list (a3-16, `lib/usuals.ts`).
  const usuals = usualsToOffer(usualsQ.data ?? [], items, MAX_USUALS)

  /** One line can name several items (`2 kg potatoes, lemons ×4`, UI-audit
   *  a2-13), and naming one already open bumps its count rather than adding
   *  a second row (a2-12). `planGroceryAdd` decides both; this only writes. */
  async function add(text: string) {
    const out = await addGrocery({ addM, updateM, bumpM, qc, aisles: known, dict }, list, items, text)
    if (!out.ok || out.empty) return
    setDraft('')
    if (out.already.length) toast({ text: `Already on the list: ${out.already.join(', ')}` })
  }

  function addUsual(body: string) {
    addM.mutate({ listId: list.id, body, opts: { position: topPosition(items) } })
    bumpM.mutate({ listId: list.id, body })
  }

  /** a2-14: ticking speaks the same language as every other change on a
   *  list — a toast with Undo — instead of leaving "tap the circle again in
   *  the basket" as the only way back. Unticking needs none: it is itself the
   *  undo, and the row is back where you are looking. */
  function toggle(i: ListItem) {
    const ticking = !i.done_at
    toggleM.mutate({ id: i.id, done: ticking })
    if (!ticking) { linger.cancel(i.id); return }
    linger.start(i.id)
    toast({
      text: `"${i.body}" in the basket`,
      action: { label: 'Undo', onClick: () => { linger.cancel(i.id); toggleM.mutate({ id: i.id, done: false }) } },
    })
  }

  function saveShop() {
    setShopEdit(false)
    const next = shop.trim() || null
    if (next !== (list.shop ?? null)) shopM.mutate({ id: list.id, shop: next })
  }

  // F7: removal is a swipe and a toast, never a confirmation dialog. Undo
  // writes the row straight back with every column it had — `qty` and
  // `done_at` included — the same shape `ListDetail.tsx`'s own `restore` is.
  async function restore(i: ListItem, gone: Promise<unknown>) {
    const { error: err } = await restoreItem(i, gone)
    if (!err) void qc.invalidateQueries({ queryKey: ['list_items'] })
  }

  const del = (i: ListItem) => {
    // The delete's own promise, so Undo writes the row back after it and
    // not before (a2-14): the row returns under its own id now, which a
    // delete still in flight would otherwise remove a second time.
    const gone = removeM.mutateAsync(i.id).catch(() => {})
    toast({ text: `Removed "${i.body}"`, action: { label: 'Undo', onClick: () => void restore(i, gone) } })
  }

  /** L3's own foot button: hides the basket at once (an optimistic patch, no
   *  network write yet) and deletes for real once the toast has run its
   *  course. Undo just cancels the timer and refetches — nothing was ever
   *  actually removed, so there is nothing to reinsert. */
  function clearBasket() {
    if (!basket.length) return
    const ids = new Set(basket.map(i => i.id))
    qc.setQueryData<ListItem[]>(['list_items'], prev => (prev ?? []).filter(i => !ids.has(i.id)))
    const timer = setTimeout(() => { for (const id of ids) removeM.mutate(id) }, 5000)
    toast({
      text: `Cleared ${ids.size} from the basket`,
      ms: 5000,
      action: {
        label: 'Undo',
        onClick: () => { clearTimeout(timer); void qc.invalidateQueries({ queryKey: ['list_items'] }) },
      },
    })
  }

  const groups: Record<string, ListItem[]> = {}
  for (const i of open) (groups[aisleOf(i.body, known, dict ?? [])] ??= []).push(i)
  const shownOrder = order.filter(a => groups[a]?.length)

  function dropOn(target: string) {
    if (!drag || drag.aisle === target) { setDrag(null); return }
    const next = order.filter(a => a !== drag.aisle)
    next.splice(next.indexOf(target), 0, drag.aisle)
    setDrag(null)
    setOrder(next)
    saveOrder(list.id, next)
  }

  return (
    <div className="grocery">
      <div className="grocery__head">
        <span className="grocery__count">{open.length} to get</span>
        {shopEdit ? (
          <Field label="Shop" className="grocery__shopfield">
            <Field.Input
              autoFocus value={shop} placeholder="Where do you get this?"
              onChange={e => setShop(e.target.value)}
              onBlur={saveShop}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
            />
          </Field>
        ) : (
          <button type="button" className="grocery__shop" onClick={() => setShopEdit(true)}>
            {list.shop || 'Add shop'}
          </button>
        )}
      </div>

      {usuals.length > 0 && (
        <div className="grocery__usuals">
          {usuals.map(u => <Chip key={u.body} onClick={() => addUsual(u.body)}>{u.body}</Chip>)}
        </div>
      )}

      {error && <Banner tone="bad">{error}</Banner>}

      <div className="lists-addrow">
        <Field label="Add" className="lists-addrow__field">
          <Field.Input
            value={draft} placeholder="2 kg potatoes, lemons ×4" autoComplete="off"
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void add(draft) }}
          />
        </Field>
        <Button variant="primary" disabled={busy || !draft.trim()} onClick={() => void add(draft)}>Add</Button>
        <IconButton label="Paste a recipe" onClick={() => setRecipeOpen(true)}><ClipboardPaste size={20} /></IconButton>
      </div>

      {!open.length && !basket.length ? (
        <div className="stub">All clear.</div>
      ) : (
        <div className="lists-group">
          {shownOrder.map(a => (
            <div
              key={a}
              draggable
              className={`draggable${drag?.aisle === a ? ' is-dragging' : ''}${drag?.over === a && drag.aisle !== a ? ' is-over' : ''}`}
              onDragStart={() => setDrag({ aisle: a, over: null })}
              onDragOver={ev => { if (drag) { ev.preventDefault(); if (drag.over !== a) setDrag({ ...drag, over: a }) } }}
              onDrop={ev => { ev.preventDefault(); dropOn(a) }}
              onDragEnd={() => setDrag(null)}
            >
              <p className="lists-grouphead"><span>{a}</span></p>
              {groups[a].map(i => (
                <GroceryRow
                  key={i.id} item={i} editing={edit === i.id} leaving={linger.leaving.has(i.id)}
                  onToggle={() => toggle(i)}
                  onDelete={() => del(i)}
                  onEdit={() => setEdit(i.id)}
                  onCancel={() => setEdit(null)}
                  onSave={line => {
                    setEdit(null)
                    const parsed = parseQty(line)
                    updateM.mutate({ id: i.id, patch: { body: parsed.body, qty: parsed.qty } })
                  }}
                  onQty={qty => updateM.mutate({ id: i.id, patch: { qty } })}
                  onMove={() => setMoveItem(i)}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {basket.length > 0 && (
        <div className="grocery__basket">
          <p className="lists-grouphead"><span>In the basket · {basket.length}</span></p>
          <div className="lists-group">
            {basket.map(i => (
              <GroceryRow
                key={i.id} item={i} editing={false}
                onToggle={() => toggle(i)} onDelete={() => del(i)} onSave={() => {}} onCancel={() => {}}
                onQty={qty => updateM.mutate({ id: i.id, patch: { qty } })}
                onMove={() => setMoveItem(i)}
              />
            ))}
          </div>
          <Button variant="quiet" size="sm" onClick={clearBasket}>Clear basket</Button>
        </div>
      )}

      {recipeOpen && (
        <RecipeImportSheet
          list={list} items={items} known={known} dict={dict}
          initialText={recipe && recipe !== 'clipboard' ? recipe : ''}
          onClose={() => { setRecipeOpen(false); onRecipeDone?.() }}
        />
      )}

      {moveItem && (
        <MoveToSheet
          item={moveItem}
          onClose={() => setMoveItem(null)}
          onPick={aisle => {
            setAisleM.mutate({ text: moveItem.body, aisle })
            setMoveItem(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * "Move to…" — the person's own correction, long-pressed from any row.
 * Writes `grocery_aisle_overrides` for the item's normalised term (and, for
 * an admin, the shared `grocery_aisles` row too — `useSetAisleOverride`),
 * which wins over the seed and over anything the Worker has learned the
 * next time this or any other list reads it. All
 * nine aisles are offered, `'Other'` included — filing something there on
 * purpose is still a decision worth remembering, not the absence of one.
 */
function MoveToSheet({ item, onClose, onPick }: {
  item: ListItem
  onClose: () => void
  onPick: (aisle: string) => void
}) {
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title={`Move "${item.body}"`}>
      {AISLES.map(a => (
        <Row key={a} className="u-row--list" leading={null} title={a} meta={null} onPress={() => onPick(a)} />
      ))}
    </Sheet>
  )
}

/** The trailing slot's `−`/`+`: a count of 1 is never shown, since that is
 *  what having no quantity already means, so `+` from nothing starts at ×2
 *  and `−` at ×2 clears it rather than landing on ×1. */
function CountStepper({ qty, onChange }: { qty: string | null; onChange: (next: string | null) => void }) {
  const n = qty?.startsWith('×') ? Number(qty.slice(1)) : null
  return (
    <span className="grocery__stepper">
      <IconButton
        label="Fewer" size="sm" disabled={n == null}
        onClick={() => onChange(n === 2 ? null : `×${(n ?? 2) - 1}`)}
      >
        <Minus />
      </IconButton>
      <span className="grocery__stepnum">{n ?? ''}</span>
      <IconButton label="More" size="sm" onClick={() => onChange(`×${(n ?? 1) + 1}`)}>
        <Plus />
      </IconButton>
    </span>
  )
}

function GroceryRow({ item, editing, leaving, onToggle, onDelete, onEdit, onSave, onCancel, onQty, onMove }: {
  item: ListItem; editing: boolean
  /** The linger's last act: the row collapses out of its aisle. */
  leaving?: boolean
  /** Absent in the basket: a ticked item is not edited, only unticked. */
  onToggle: () => void; onDelete: () => void; onEdit?: () => void
  onSave: (line: string) => void; onCancel: () => void; onQty: (qty: string | null) => void; onMove: () => void
}) {
  const [draft, setDraft] = useState(() => formatLine(item.body, item.qty))
  // Escape puts the line back and leaves edit mode without a write, the same
  // keys a task title has (UI-audit a2-10).
  const keys = useBlurField(() => { setDraft(formatLine(item.body, item.qty)); onCancel() })
  if (editing) {
    return (
      <Row
        className="u-row--list"
        leading={<Checkbox size="md" checked={!!item.done_at} onChange={onToggle} aria-label={`Mark "${item.body}" done`} />}
        title={
          <Field.Input
            autoFocus value={draft} className="grocery__editfield"
            onChange={e => setDraft(e.target.value)}
            onBlur={keys.onBlurOf(() => onSave(draft))}
            onKeyDown={keys.onKeyDown}
          />
        }
        meta={null}
      />
    )
  }
  const isAmount = !!item.qty && !item.qty.startsWith('×')
  return (
    <Row
      className="u-row--list"
      leaving={leaving}
      leading={
        <Checkbox
          size="md"
          checked={!!item.done_at}
          onChange={onToggle}
          aria-label={item.done_at ? `Mark "${item.body}" not done` : `Mark "${item.body}" done`}
        />
      }
      title={
        <button type="button" className={`lists-itembody${item.done_at ? ' is-done' : ''}`} onClick={onEdit}>
          {item.body}
        </button>
      }
      meta={null}
      trailing={
        <>
          {isAmount
            ? <button type="button" className="grocery__amount" onClick={onEdit}>{item.qty}</button>
            : <CountStepper qty={item.qty} onChange={onQty} />}
          <RowMore
            name={item.body} onEdit={onEdit} onRemove={onDelete}
            extra={[{ label: 'Move to aisle', icon: <ArrowRightLeft size={20} aria-hidden="true" />, onSelect: onMove }]}
          />
        </>
      }
      swipe={{ left: { label: 'Remove', onAction: onDelete, leaves: true } }}
      onLongPress={onMove}
    />
  )
}
